import type { VehicleBodyType, VehicleCondition, VehicleFuel, VehicleTransmission } from '@/lib/sanity/dto';
import {
    VEHICLE_BODY_TYPE_LABELS,
    VEHICLE_CONDITION_FILTER_LABELS,
    VEHICLE_FUEL_LABELS,
    VEHICLE_TRANSMISSION_LABELS,
} from '@/features/vehicle';
import {
    isRestricted,
    OPEN_RANGE,
    thumbsOf,
    toggleMake,
    VEHICLE_RANGE_FACETS,
    type VehicleFilterBounds,
    type VehicleFilters,
    type VehicleListFacet,
    type VehicleRangeFacet,
} from './vehicleFilters';
import { rangeLabel } from './vehicleRangeLabels';

/** One removable chip per active filter value. */
export type VehicleActiveFilter =
    | { id: string; label: string; facet: VehicleListFacet; value: string }
    | { id: string; label: string; facet: VehicleRangeFacet };

function listChips<T extends string>(
    facet: VehicleListFacet,
    values: T[],
    label: (value: T) => string,
): VehicleActiveFilter[] {
    return values.map((value) => ({ id: `${facet}:${value}`, label: label(value), facet, value }));
}

/** The same „von – bis“ as above the slider; an open end shows the edge of the scale. */
function rangeChip(
    facet: VehicleRangeFacet,
    filters: VehicleFilters,
    bounds: VehicleFilterBounds,
): VehicleActiveFilter[] {
    const range = bounds[facet];
    const selection = filters[facet];
    if (!range || !isRestricted(selection)) return [];

    return [{ id: facet, facet, label: rangeLabel(facet, thumbsOf(selection, range)) }];
}

function rangeChips(filters: VehicleFilters, bounds: VehicleFilterBounds): VehicleActiveFilter[] {
    return VEHICLE_RANGE_FACETS.flatMap((facet) => rangeChip(facet, filters, bounds));
}

/** In the design's order: condition, make, model, type, price, mileage, year, fuel, transmission. */
export function activeFilters(filters: VehicleFilters, bounds: VehicleFilterBounds): VehicleActiveFilter[] {
    return [
        ...listChips<VehicleCondition>(
            'condition',
            filters.condition,
            (value) => VEHICLE_CONDITION_FILTER_LABELS[value],
        ),
        ...listChips('make', filters.make, (value) => value),
        ...listChips('model', filters.model, (value) => value.split('|')[1] || value),
        ...listChips<VehicleBodyType>('bodyType', filters.bodyType, (value) => VEHICLE_BODY_TYPE_LABELS[value]),
        ...rangeChips(filters, bounds),
        ...listChips<VehicleFuel>('fuel', filters.fuel, (value) => VEHICLE_FUEL_LABELS[value]),
        ...listChips<VehicleTransmission>(
            'transmission',
            filters.transmission,
            (value) => VEHICLE_TRANSMISSION_LABELS[value],
        ),
    ];
}

export function removeActiveFilter(filters: VehicleFilters, chip: VehicleActiveFilter): VehicleFilters {
    if (!('value' in chip)) return { ...filters, [chip.facet]: OPEN_RANGE };
    if (chip.facet === 'make') return toggleMake(filters, chip.value);

    const values = filters[chip.facet] as string[];
    return { ...filters, [chip.facet]: values.filter((value) => value !== chip.value) };
}
